import React from "react";
import { Skeleton } from "@mui/material";
import useTheme from "../../../../common/hooks/useTheme";

/**
 * Placeholders for the Dashboard blocks backed by `dataPlatformServer`
 * (GetReportv2, ~8s). The nutrition-backed column already has
 * `HomeSectionSkeleton`; these cover the report-backed blocks, which previously
 * had no loading state because the shell blocked until the data arrived.
 *
 * They double as the render guard: `dataPlatformServer` starts as
 * `{ prescriptionPlan: [] }` with every other key undefined, so the real
 * components must not mount before the report lands.
 *
 * Matches the surrounding convention (MUI Skeleton, `N[30]`, 8px radius) — see
 * `CoreMetricCard` and `HomeSectionSkeleton`.
 */

const useBg = () => {
  const theme = useTheme();
  return theme.colorTokens.N[30];
};

/**
 * Bordered card wrapper mirroring OverviewMetricsWidget's container — including
 * its `marginTop: 32px`. Without that margin the skeletons sat flush against the
 * CoreMetricCard row and each other, so the second column visibly re-spaced when
 * the real widgets replaced them.
 */
const Card = ({ children }: { children: React.ReactNode }) => {
  const theme = useTheme();
  return (
    <div
      style={{
        border: `1px solid ${theme.colors_ds.NEUTRAL_BORDER_WEAK}`,
        marginTop: "32px",
        padding: "8px",
        borderRadius: "8px",
        backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
        width: "100%",
      }}
    >
      {children}
    </div>
  );
};

/** "PNOE scores" / "Areas of focus" — title plus a short list of rows. */
export const MetricsWidgetSkeleton = ({ rows = 4 }: { rows?: number }) => {
  const bg = useBg();
  return (
    <Card>
      <Skeleton
        variant="text"
        sx={{ bgcolor: bg, width: "40%", height: "24px" }}
      />
      {Array.from({ length: rows }).map((_, index) => (
        <Skeleton
          key={index}
          variant="rectangular"
          sx={{
            bgcolor: bg,
            height: "32px",
            borderRadius: "8px",
            marginTop: "8px",
          }}
        />
      ))}
    </Card>
  );
};

/** Single report-backed card (StartConsultationCard, NextBreathAnalysis). */
export const ReportCardSkeleton = ({ height = 120 }: { height?: number }) => {
  const bg = useBg();
  return (
    <Skeleton
      variant="rectangular"
      sx={{
        bgcolor: bg,
        width: "100%",
        height: `${height}px`,
        borderRadius: "8px",
      }}
    />
  );
};

/**
 * Stand-in for UserProgress in the header row. Mirrors its container geometry
 * (`margin: 16px 150px 0`, `flex: 1`) so the Charts button keeps its position
 * instead of sliding left while the report loads.
 */
export const UserProgressSkeleton = () => {
  const bg = useBg();
  return (
    <div style={{ margin: "16px 150px 0", flex: 1 }}>
      <Skeleton
        variant="rectangular"
        sx={{ bgcolor: bg, height: "40px", borderRadius: "8px" }}
      />
    </div>
  );
};

/** Services strip — title row plus two side-by-side cards. */
export const ServicesSectionSkeleton = () => {
  const bg = useBg();
  return (
    <div style={{ width: "100%" }}>
      <Skeleton
        variant="text"
        sx={{ bgcolor: bg, width: "30%", height: "24px" }}
      />
      <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
        {[0, 1].map((index) => (
          <Skeleton
            key={index}
            variant="rectangular"
            sx={{
              bgcolor: bg,
              flex: 1,
              height: "140px",
              borderRadius: "8px",
            }}
          />
        ))}
      </div>
    </div>
  );
};
